/**
 * Stop inspector: edit the selected stop's fields and the selected timeline
 * action. Mirrors the mockup's inspector + buildCommandBody. All inputs are
 * disabled (read-only) unless the matching handler is supplied (Phase B).
 */
import { useEffect, useRef, useState } from 'react';
import {
  CHECK_TYPES, CHECK_TYPE_LABEL, CMD_DESC, CMD_FIELDS, CMD_LABEL, CMD_PAIR, CONTINUOUS_CMDS,
  LANES, addWorldSelectionTwin, authoredBeats, canonicalCmd, checkError, commandPoints, estimateDwellMinutes, flyLookTargets, isPathCommand, isWorldSelection,
  hasGradingChoice, setCheckGrading, withCheckOptions,
  laneOf, narrMs, previewableCommand, switchCheckType, twinOptionsForCommand, uid, withCommandPoints, worldSelectionTwinIds,
  type CheckTypeTag, type CommandAction, type CommandTypeTag, type EditorAction,
  type EditorCheck, type EditorCheckOption, type EditorStep,
  type NarrateAction, type VersionTag, type WaitAction,
} from './model';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';
import { PortalIcons } from '../portalIcons';
import type { CommandLocation, CommandTarget } from '@/generated/minetwin/types';
import { formatLegacyVersionTag } from '@/lib/orientationVersion';
import { FloatPanel, type FloatShell } from './PanelShell';
import { SearchSelect } from './SearchSelect';
import { elevationAt } from './terrainElevation';
import { useUnits } from '@/hooks/useUnits';

/** Every location slot pick-in-scene can fill: the stop's own coords, the
 *  selected geo command's, or one of Fly + look's two CommandTargets. */
export type PickTarget = 'stop' | 'action' | 'flyTarget' | 'lookAtTarget' | 'pathNode';

type InspectorProps = {
  step: EditorStep | null;
  /** Legacy timed slots that still hold a script for this stop. The editor
   *  neither renders nor saves them; naming them here is what stops an empty
   *  Default timeline from reading as data loss (Phase 4 migration pending). */
  legacyVersions: VersionTag[];
  selectedAction: EditorAction | null;
  /** The selected comprehension check, or null. Mutually exclusive with
   *  `selectedAction`: the editor clears one when the other is picked. */
  selectedCheck?: EditorCheck | null;
  onCheckChange?: (next: EditorCheck) => void;
  onDeleteCheck?: () => void;
  /** Stable key of the SELECTION coordinates (step|beat|action) —
   *  drives scroll-into-view without re-firing on every draft clone. */
  actionKey?: string | null;
  twinOptions: string[];
  /** twinId → display name (the twin's "displayName" string property), used to
   *  decorate the Target-twin dropdown; ids stay searchable via the value. */
  twinNames?: Map<string, string>;
  /** twinId → its geo property names (complex_property Geospatial rows).
   *  Filters HighlightGeo's twin list and feeds its property dropdown. */
  geoPropsByTwin?: Map<string, string[]>;
  /** twinId → ALL its property names, for ShowTwinInfo's Properties picker. */
  propsByTwin?: Map<string, string[]>;
  /** twinId → its telemetry names, for ShowTwinInfo's Telemetries picker. */
  telemetryNamesByTwin?: Map<string, string[]>;
  /** twinId → its LineString geospatial properties as ready waypoint lists, for
   *  "Fly along path"'s Twin source (pick a path twin → import its spline). */
  lineStringsByTwin?: Map<string, { name: string; points: CommandLocation[] }[]>;
  /** Twins whose turbotwin_metadata location mode is Dynamic — the only ones
   *  FollowTwin can target. */
  dynamicTwinIds?: Set<string>;
  /** How many orientations use this step (>1 means edits propagate to all). */
  usedIn?: number;
  onStepPatch?: (patch: Partial<Pick<EditorStep, 'name' | 'durationMinutes' | 'lat' | 'lon' | 'el'>>) => void;
  onActionChange?: (next: EditorAction) => void;
  onDeleteAction?: () => void;
  /** Copy this stop (scripts and checks included): a shared stop is replaced
   *  here by the copy, an unshared one gets the copy added next to it. */
  onCopyStop?: () => void;
  onRemoveStop?: () => void;
  /** Arm (or disarm) pick-in-scene for a Location row: the next click in the
   *  2D map or 3D view fills that location (the stop's, the selected geo
   *  command's, or a Fly + look Location target's). */
  onPickLocation?: (target: PickTarget) => void;
  /** Arm a ground pick that fills ONE waypoint (by index) of a multi-location
   *  FlyToLocation. 3D-only; absent = hide the per-node Pick buttons. */
  onPickPathNode?: (index: number) => void;
  /** Fill ONE waypoint (by index) from the camera's own position. 3D-only. */
  onPlacePathNodeAtCamera?: (index: number) => void;
  /** Which waypoint's ground-pick is currently armed (lights that node's Pick
   *  button), or null. */
  activePathIndex?: number | null;
  /** Capture the live 3D camera framing into a Fly + look command (fly = camera
   *  position, look = focus point, both as locations). Supplied only when the 3D
   *  pane is active; absent = hide the button. */
  onCaptureView?: () => void;
  /** Which Location row's pick is currently armed — lights its pick button. */
  pickTarget?: PickTarget | null;
  /** Preview the selected camera command in the embedded 3D viewer. Supplied
   *  only when the 3D pane is active; absent = hide the button (nothing to
   *  drive in 2D). Only shown for commands `previewableCommand` accepts. */
  onPreviewCommand?: (a: CommandAction) => void;
  /** Preview a narration line in 3D (its caption over the viewer). Supplied only
   *  when the 3D pane is active. */
  onPreviewNarration?: (a: NarrateAction) => void;
  /** Preview a comprehension check in 3D (present it as playback does). Supplied
   *  only when the 3D pane is active. */
  onPreviewCheck?: (c: EditorCheck) => void;
  /** Whether the selected check is currently being previewed — flips its button
   *  caption to "Stop Preview". */
  checkPreviewActive?: boolean;
  /** The twin currently selected in the 3D scene, or null. Drives the "Assign
   *  selected" button on a command's Target-twin field so an author can retarget
   *  an existing beat from the 3D view. Supplied only when the 3D pane is active
   *  (absent = hide the button). */
  selected3dTwin?: string | null;
  /** Stop a running continuous preview (Orbit / Follow). */
  onStopPreview?: () => void;
  /** Whether the selected command is currently being continuously previewed —
   *  flips its button caption to "Stop Preview". */
  previewActive?: boolean;
  dirty?: boolean;
  /** Floating-card chrome from useEditorLayout. */
  shell: FloatShell;
};

export function Inspector(props: InspectorProps) {
  const { step, legacyVersions, selectedAction, selectedCheck, onCheckChange, onDeleteCheck, actionKey, twinOptions, twinNames, selected3dTwin, onPreviewCheck, onStopPreview, checkPreviewActive, usedIn, onStepPatch, onCopyStop, onRemoveStop, onPickLocation, pickTarget, dirty, shell } = props;

  // Selecting a timeline block must actually show its editor: scroll it into
  // view (the panel body may be scrolled up to the stop fields). Keyed on the
  // selection coordinates, NOT the action object — the draft reclones every
  // action on every keystroke, and firing then would yank the scroll away
  // from the field being typed in.
  const actRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (actionKey) actRef.current?.scrollIntoView({ block: 'nearest' });
  }, [actionKey]);

  // Dwell follows the script estimate (checkbox on, input locked) until the
  // author unticks it. Unticking alone does not change the value, so remember
  // it locally until they type something else; a new stop starts ticked.
  const [manualDwell, setManualDwell] = useState(false);
  useEffect(() => setManualDwell(false), [step?.stepId]);
  const dwellEstimate = step ? estimateDwellMinutes(step) : 0;
  const useEstimate = !!step && step.durationMinutes === dwellEstimate && !manualDwell;

  return (
    <FloatPanel
      shell={shell}
      title="Stop details"
      sub={step && (usedIn ?? 0) > 1 ? (
        <span className="oe-reuse-badge" title={`Shared: used in ${usedIn} orientations. Edits apply to all of them.`}>
          <Icon of={PortalIcons.status.shared} size={12} /> used in {usedIn}
        </span>
      ) : undefined}
      headExtra={
        <>
          {/* Auto-apply model: edits commit on their own (debounced), so the
              dot is a transient syncing cue rather than a call to action. */}
          {step && dirty && <span className="oe-dirty" title="Applying changes…"><Icon of={PortalIcons.status.live} size={8} /> saving…</span>}
          {step && onCopyStop && (
            <button
              className="oe-ibtn"
              aria-label="Copy stop"
              onClick={onCopyStop}
              title={(usedIn ?? 0) > 1
                ? 'Replace this shared stop here with your own copy, so edits no longer touch the other orientations'
                : 'Add a copy of this stop to the orientation'}
            ><Icon of={Icons.action.copy} size={14} /></button>
          )}
          {step && onRemoveStop && ((usedIn ?? 0) > 1 ? (
            <button
              className="oe-ibtn"
              aria-label="Remove stop from this orientation"
              onClick={onRemoveStop}
              title="Remove this stop from the orientation (it stays, other orientations use it)"
            ><Icon of={Icons.action.unlink} size={14} /></button>
          ) : (
            <button
              className="oe-ibtn del"
              aria-label="Delete stop"
              onClick={onRemoveStop}
              title="Delete this stop (only this orientation uses it; you can still choose to keep it)"
            ><Icon of={Icons.action.delete} size={14} /></button>
          ))}
        </>
      }
    >
      <div className="oe-panel-body" style={{ padding: 12 }}>
        {!step ? (
          <div className="oe-empty">
            <h4>Select a stop</h4>
            Pick a stop to view or edit its details.
          </div>
        ) : (
          <>
            {/* The stop fields sit under the panel head itself, which is the
                "Stop details" title with Copy and Remove: no second header row. */}
            <div className="oe-sect-body">
              <Field label="Stop name">
                <input className="oe-inp" value={step.name} disabled={!onStepPatch}
                  onChange={(e) => onStepPatch?.({ name: e.target.value })} />
              </Field>

              <div className="oe-field">
                <label title="Where the trainee stands at this stop (WGS84 + elevation). Edit the numbers, drag the map marker, or pick in scene.">Location</label>
                <LocationField
                  lat={step.lat}
                  lon={step.lon}
                  alt={step.el}
                  onChange={onStepPatch ? (v) => onStepPatch({ lat: v.lat, lon: v.lon, el: v.alt }) : undefined}
                  onPick={onPickLocation ? () => onPickLocation('stop') : undefined}
                  picking={pickTarget === 'stop'}
                />
              </div>

              <Field label="Dwell (min)" title="Expected minutes a trainee spends at this stop. Follows the script estimate while Use estimate is ticked. Feeds progress metrics (a visit under half this time is flagged as rushed); it does not pace script playback.">
                <div className="oe-dwell">
                  <input className="oe-inp mono" type="number" min={0} value={step.durationMinutes} disabled={!onStepPatch || useEstimate}
                    onChange={(e) => onStepPatch?.({ durationMinutes: parseInt(e.target.value || '0', 10) })} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                    title={`Follow the script estimate (${dwellEstimate} min); untick to type your own value`}>
                    <input type="checkbox" checked={useEstimate} disabled={!onStepPatch}
                      onChange={(e) => {
                        setManualDwell(!e.target.checked);
                        if (e.target.checked) onStepPatch?.({ durationMinutes: dwellEstimate });
                      }} />
                    <span>Use estimate</span>
                  </label>
                </div>
              </Field>

              {legacyVersions.length > 0 && (
                <div className="oe-hint" style={{ margin: '-4px 0 10px' }}>
                  Older script still stored under:{' '}
                  {legacyVersions.map((v, i) => (
                    <span key={v} className="mono" style={{ color: 'var(--text-2)' }}>
                      {i > 0 ? ', ' : ''}{formatLegacyVersionTag(v) ?? v}
                    </span>
                  ))}
                  . It is not shown or edited here.
                </div>
              )}
            </div>

            <Section title={selectedCheck ? 'Selected check' : 'Selected action'}>
              {selectedCheck ? (
                <div ref={actRef}>
                  <CheckEditor check={selectedCheck} step={step} twinOptions={twinOptions} twinNames={twinNames} selected3dTwin={selected3dTwin} onChange={onCheckChange} onDelete={onDeleteCheck} onPreview={onPreviewCheck} onStopPreview={onStopPreview} previewActive={checkPreviewActive} />
                </div>
              ) : !selectedAction ? (
                <div className="oe-empty" style={{ padding: 14 }}>
                  Click an action or check block in the timeline to {onStepPatch ? 'edit' : 'view'} it.
                </div>
              ) : (
                <div ref={actRef}>
                  {/* Key by the selection so per-action editor state (e.g. the
                      path Source toggle) resets when a different block is picked. */}
                  <ActionEditor key={actionKey ?? 'action'} {...props} action={selectedAction} />
                </div>
              )}
            </Section>
          </>
        )}
      </div>
    </FloatPanel>
  );
}

function ActionEditor({ action, step, twinOptions, twinNames, geoPropsByTwin, propsByTwin, telemetryNamesByTwin, lineStringsByTwin, dynamicTwinIds, onActionChange, onDeleteAction, onPreviewCommand, onPreviewNarration, onStopPreview, previewActive, onPickLocation, onPickPathNode, onPlacePathNodeAtCamera, activePathIndex, onCaptureView, pickTarget, selected3dTwin }: InspectorProps & { action: EditorAction }) {
  const editable = !!onActionChange;
  // For command actions, surface which lane (Camera / Effects) the command
  // belongs to next to the kind badge.
  const lane = action.kind === 'command' ? LANES.find((l) => l.key === laneOf(action)) : undefined;
  return (
    <div className="oe-act">
      <div className="oe-act-head">
        <span
          className={'oe-kind ' + action.kind}
          title={action.kind === 'command'
            ? "A 3D instruction sent to every participant's client when its beat plays"
            : action.kind === 'narrate'
            ? 'Spoken narration (text-to-speech) with an on-screen caption'
            : 'A timed hold on this beat'}
        >{action.kind}</span>
        {lane && <span className={'oe-lane-tag ' + lane.key} title={lane.desc}>{lane.label}</span>}
        <span className="oe-spacer" />
        {editable && onDeleteAction && (
          <button className="oe-ibtn del" aria-label="Delete action" title="Delete action" onClick={onDeleteAction}><Icon of={Icons.action.delete} size={14} /></button>
        )}
      </div>
      <div className="oe-act-body">
        {action.kind === 'command' && (
          <CommandBody a={action} step={step} twinOptions={twinOptions} twinNames={twinNames} geoPropsByTwin={geoPropsByTwin} propsByTwin={propsByTwin} telemetryNamesByTwin={telemetryNamesByTwin} lineStringsByTwin={lineStringsByTwin} dynamicTwinIds={dynamicTwinIds} selected3dTwin={selected3dTwin} onChange={onActionChange} onPreview={onPreviewCommand} onStopPreview={onStopPreview} previewActive={previewActive} onPickLocation={onPickLocation} onPickPathNode={onPickPathNode} onPlacePathNodeAtCamera={onPlacePathNodeAtCamera} activePathIndex={activePathIndex} onCaptureView={onCaptureView} pickTarget={pickTarget} />
        )}
        {action.kind === 'narrate' && (
          <NarrateBody a={action} onChange={onActionChange} onPreview={onPreviewNarration} onStopPreview={onStopPreview} previewActive={previewActive} />
        )}
        {action.kind === 'wait' && (
          <WaitBody a={action} onChange={onActionChange} />
        )}
      </div>
    </div>
  );
}

function CommandBody({ a, step, twinOptions, twinNames, geoPropsByTwin, propsByTwin, telemetryNamesByTwin, lineStringsByTwin, dynamicTwinIds, selected3dTwin, onChange, onPreview, onStopPreview, previewActive, onPickLocation, onPickPathNode, onPlacePathNodeAtCamera, activePathIndex, onCaptureView, pickTarget }: {
  a: CommandAction; step: EditorStep | null; twinOptions: string[];
  twinNames?: Map<string, string>;
  geoPropsByTwin?: Map<string, string[]>;
  propsByTwin?: Map<string, string[]>;
  telemetryNamesByTwin?: Map<string, string[]>;
  lineStringsByTwin?: Map<string, { name: string; points: CommandLocation[] }[]>;
  dynamicTwinIds?: Set<string>;
  selected3dTwin?: string | null;
  onChange?: (next: EditorAction) => void;
  onPreview?: (a: CommandAction) => void;
  onStopPreview?: () => void;
  previewActive?: boolean;
  onPickLocation?: (target: PickTarget) => void;
  onPickPathNode?: (index: number) => void;
  onPlacePathNodeAtCamera?: (index: number) => void;
  activePathIndex?: number | null;
  onCaptureView?: () => void;
  pickTarget?: PickTarget | null;
}) {
  const patch = (p: Partial<CommandAction>) => onChange?.({ ...a, ...p });
  const f = CMD_FIELDS[a.commandType] ?? [];
  const { lengthUnit, fmtLength, fromMetres } = useUnits();
  const unitWord = lengthUnit === 'ft' ? 'feet' : 'metres';
  const continuousCapable = CONTINUOUS_CMDS.includes(a.commandType);
  const continuous = continuousCapable && a.durationMs == null;
  // Only offer commands from the same lane as the current one (a Camera command
  // can only switch to another Camera command, an Effects one to another Effects).
  const laneCmds = (LANES.find((l) => (l.cmds as readonly string[]).includes(a.commandType))?.cmds
    ?? [a.commandType]) as readonly CommandTypeTag[];
  // Twin/geo variants collapse to one dropdown entry (the twin arm); the
  // Target toggle below switches between the pair's concrete command types.
  const pair = CMD_PAIR[a.commandType];
  const isGeoArm = pair?.geo === a.commandType;
  // "Fly along path" (FlyAlongPath) is a first-class camera command — see
  // isPathCommand. In path mode the Target (Twin|Location) toggle is hidden and
  // the waypoint editor replaces the single Location field.
  const pathMode = isPathCommand(a);
  const cmdOptions = laneCmds.filter((c) => CMD_PAIR[c]?.geo !== c);
  // Capability-filtered twin list for the current command (HighlightGeo: only
  // twins with geo properties; Follow: only Dynamic-location twins).
  const cmdTwinOptions = twinOptionsForCommand(a.commandType, twinOptions, geoPropsByTwin, dynamicTwinIds);
  const staleNote =
    a.commandType === 'HighlightGeo' ? '— no geo properties'
    : a.commandType === 'FollowTwin' ? '— not a dynamic twin'
    : undefined;

  return (
    <>
      <Field label="Command">
        <SearchSelect
          value={canonicalCmd(a.commandType)}
          disabled={!onChange}
          options={cmdOptions.map((c) => ({ value: c as string, label: CMD_LABEL[c] ?? c, title: CMD_DESC[c] }))}
          onChange={(v) => {
            const tag = v as CommandTypeTag;
            if (tag === 'FlyAlongPath') {
              if (pathMode) return; // already a path command
              // Enter path mode: seed the waypoint list from the current location
              // (or the stop). If it has no explicit altitude, snap the seed onto
              // the terrain so it never starts below ground.
              const seed = commandPoints(a);
              if (seed.length) {
                onChange?.(withCommandPoints({ ...a, commandType: 'FlyAlongPath' }, seed));
                return;
              }
              const lat = a.latitude ?? step?.lat ?? 0;
              const lon = a.longitude ?? step?.lon ?? 0;
              const base = { ...a, commandType: 'FlyAlongPath' as const };
              void elevationAt(lat, lon).then((el) =>
                onChange?.(withCommandPoints(base, [{ latitude: lat, longitude: lon, altitude: el ?? a.altitude ?? step?.el ?? undefined }])));
              return;
            }
            // Leaving path mode (or switching command) sets the picked type and
            // drops any path, so it reads as a plain command again. SetCameraMode
            // keeps its mode in `message`: seed a default when entering it, and
            // clear it when leaving so a stale mode never rides another command.
            if (pathMode || tag !== canonicalCmd(a.commandType)) {
              const enteringMode = tag === 'SetCameraMode' && a.commandType !== 'SetCameraMode';
              const leavingMode = a.commandType === 'SetCameraMode' && tag !== 'SetCameraMode';
              patch({
                commandType: tag,
                path: [],
                ...(enteringMode ? { message: 'freeFly' } : {}),
                ...(leavingMode ? { message: null } : {}),
              });
            }
          }}
        />
        {/* What the CURRENT concrete variant does (updates with the Target
            toggle below, unlike the dropdown's canonical option titles). */}
        <div className="oe-hint" style={{ margin: '4px 0 0' }}>
          {pathMode ? 'Fly the camera along the waypoints in order.' : CMD_DESC[a.commandType]}
        </div>
      </Field>

      {pair && !pathMode && (
        <Field label="Target" title="This command comes in two variants; pick what it should act on. Switching keeps the other settings.">
          <div className="oe-toggle2">
            <button className={!isGeoArm ? 'on' : ''} disabled={!onChange}
              title={CMD_DESC[pair.twin]}
              onClick={() => patch({ commandType: pair.twin })}>Twin</button>
            <button className={isGeoArm ? 'on' : ''} disabled={!onChange}
              title={CMD_DESC[pair.geo]}
              onClick={() => patch({ commandType: pair.geo })}>
              {pair.geo === 'HighlightGeo' ? 'Geo property' : 'Location'}
            </button>
          </div>
        </Field>
      )}

      {f.includes('mode') && (
        <Field label="Camera mode" title="How the trainee's camera navigates from this beat onward: Free fly (6-DoF flight), Orbit (orbit the scene), or Walk (first-person on the ground).">
          <SearchSelect
            value={a.message ?? 'freeFly'}
            disabled={!onChange}
            options={[
              { value: 'freeFly', label: 'Free fly', title: 'Free 6-DoF flight.' },
              { value: 'orbit', label: 'Orbit', title: 'Orbit around the scene.' },
              { value: 'walk', label: 'Walk', title: 'First-person, walking on the ground.' },
            ]}
            onChange={(v) => patch({ message: v })}
          />
        </Field>
      )}

      {f.includes('twin') && (
        <Field label="Target twin" title={
          a.commandType === 'HighlightGeo' ? 'Only twins with at least one geo property are listed.'
          : a.commandType === 'FollowTwin' ? 'Only twins with a Dynamic location mode are listed (a static twin never moves).'
          : 'The twin this command acts on.'
        }>
          {/* The twin selector with an icon-only "assign from the 3D selection"
              button beside it, styled like the Fly-To-Geo pick button. Lets an
              author retarget an EXISTING beat from the 3D view (new beats
              auto-seed from the selection; this covers editing). The button
              shows only in 3D (selected3dTwin supplied); its arrow points into
              the selector, and it goes active/disabled once that twin is
              already the target. */}
          <div className="oe-loc-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <TwinSelect
                value={a.twinIds[0] ?? ''}
                twinOptions={cmdTwinOptions}
                twinNames={twinNames}
                staleNote={staleNote}
                onChange={onChange ? (v) => patch({ twinIds: [v] }) : undefined}
              />
            </div>
            {onChange && selected3dTwin && (
              <AssignSelectedButton
                selected3dTwin={selected3dTwin}
                twinNames={twinNames}
                current={a.twinIds[0] ?? ''}
                onAssign={() => patch({ twinIds: [selected3dTwin] })}
              />
            )}
          </div>
        </Field>
      )}

      {/* Path mode (virtual "Fly along path"): the waypoint editor replaces the
          single Location field. */}
      {pathMode && (
        <PathField
          a={a}
          step={step}
          twinNames={twinNames}
          lineStringsByTwin={lineStringsByTwin}
          onChange={onChange}
          onPickNode={onPickPathNode}
          onPlaceAtCamera={onPlacePathNodeAtCamera}
          activeIndex={activePathIndex ?? null}
        />
      )}
      {pathMode && (
        // Metres above each waypoint's ground elevation (eye height). Stored in
        // meters on the unused `distance` column; applied in the 3D preview and
        // baked into the waypoint altitudes the agent sends. Empty = ~1.5 m.
        <Field label={`Eye height offset (${lengthUnit})`} title={`How far above the ground the camera flies along the path, in ${unitWord}. Leave empty for the default (~${fmtLength(1.5)}, standing eye height).`}>
          <LengthInput placeholder={String(+fromMetres(1.5).toFixed(1))}
            metres={a.eyeHeightOffset} disabled={!onChange}
            onCommit={(m) => patch({ eyeHeightOffset: m })} />
        </Field>
      )}
      {f.includes('geo') && !pathMode && (
        <Field label="Location" title="The map coordinate this command targets. Defaults to the stop's own location until edited or picked in scene.">
          <LocationField
            lat={a.latitude ?? step?.lat ?? 0}
            lon={a.longitude ?? step?.lon ?? 0}
            alt={a.altitude ?? step?.el ?? 0}
            onChange={onChange ? (v) => patch({ latitude: v.lat, longitude: v.lon, altitude: v.alt }) : undefined}
            onPick={onPickLocation ? () => onPickLocation('action') : undefined}
            picking={pickTarget === 'action'}
          />
        </Field>
      )}

      {/* Fly + look: two independent twin-or-location CommandTargets — the whole
          point of the command is flying to one while looking at the other. */}
      {f.includes('flyLook') && (() => {
        const { fly, look } = flyLookTargets(a);
        return (
          <>
            <TargetField
              label="Fly to"
              title="Where the camera flies: a twin or a map coordinate."
              value={fly}
              step={step}
              twinOptions={twinOptions}
              twinNames={twinNames}
              selected3dTwin={selected3dTwin}
              onChange={onChange ? (t) => patch({ flyTarget: t }) : undefined}
              onPick={onPickLocation ? () => onPickLocation('flyTarget') : undefined}
              picking={pickTarget === 'flyTarget'}
              pinTone="fly"
            />
            <TargetField
              label="Look at"
              title="What the camera faces during the whole flight: a twin or a map coordinate."
              value={look}
              step={step}
              twinOptions={twinOptions}
              twinNames={twinNames}
              selected3dTwin={selected3dTwin}
              onChange={onChange ? (t) => patch({ lookAtTarget: t }) : undefined}
              onPick={onPickLocation ? () => onPickLocation('lookAtTarget') : undefined}
              picking={pickTarget === 'lookAtTarget'}
            />
            {/* Exact-view capture: set BOTH targets from the live 3D camera —
                fly = its position, look = its focus point — reproducing the
                current framing. 3D only (needs the viewer's camera pose). */}
            {onChange && onCaptureView && (
              <button
                className="oe-btn sm"
                style={{ marginTop: 6 }}
                onClick={onCaptureView}
                title="Set Fly to = the current camera position and Look at = its focus point, capturing this exact 3D view"
              >
                <Icon of={PortalIcons.action.captureView} size={14} /> Capture current view
              </button>
            )}
          </>
        );
      })()}

      {/* HighlightGeo: which geo property on the target twin to highlight (the
          reducer takes twin_ids + property_names, not coordinates). Options
          are the selected twin's own geo properties; a saved name no longer
          on the twin is prepended, labeled, so it stays visible where it
          needs fixing. Single-select — a legacy multi-name row keeps its
          extra names until edited here. */}
      {f.includes('properties') && (() => {
        const props = geoPropsByTwin?.get(a.twinIds[0] ?? '') ?? [];
        const cur = a.propertyNames[0] ?? '';
        const stale = Boolean(cur) && !props.includes(cur);
        const opts = stale ? [cur, ...props] : props;
        return (
          <Field label="Geo property" title="Which of the target twin's geo properties (roads, areas, points) to highlight. The list is the selected twin's own properties.">
            <SearchSelect
              value={cur}
              disabled={!onChange}
              placeholder="Search geo properties…"
              options={opts.map((p) => ({
                value: p,
                label: stale && p === cur ? `${p} — not on this twin` : p,
              }))}
              onChange={(v) => patch({ propertyNames: [v] })}
            />
          </Field>
        );
      })()}

      {f.includes('infoProperties') && (
        <NameMultiPicker
          label="Properties"
          title="Which of the twin's properties to show in the info panel. The list is the selected twin's own property names. Leave both lists empty to show every property and telemetry."
          selected={a.propertyNames}
          available={propsByTwin?.get(a.twinIds[0] ?? '') ?? []}
          placeholder="Add a property… (none = all)"
          disabled={!onChange}
          onChange={(next) => patch({ propertyNames: next })}
        />
      )}

      {f.includes('infoTelemetries') && (
        <NameMultiPicker
          label="Telemetries"
          title="Which of the twin's telemetry streams to show in the info panel. The list is the selected twin's own telemetry names. Leave both lists empty to show every property and telemetry."
          selected={a.telemetryNames}
          available={telemetryNamesByTwin?.get(a.twinIds[0] ?? '') ?? []}
          placeholder="Add a telemetry… (none = all)"
          disabled={!onChange}
          onChange={(next) => patch({ telemetryNames: next })}
        />
      )}
      {f.includes('infoProperties') && a.propertyNames.length === 0 && a.telemetryNames.length === 0 && (
        <div className="oe-hint" style={{ margin: '-6px 0 6px' }}>
          Nothing picked: the panel shows every property and telemetry of the twin. Add names to narrow it down.
        </div>
      )}

      {/* Continuous/Timed toggle only for continuous cams that can actually take
          a duration (Orbit). Follow is ALWAYS continuous — it has no `duration`
          field, so it never shows this toggle. */}
      {continuousCapable && f.includes('duration') && (
        <Field label="Camera hold" title="Whether this camera motion runs until something stops it, or for a fixed time.">
          <div className="oe-toggle2">
            <button className={continuous ? 'on' : ''} disabled={!onChange}
              title="Keeps running across later beats until a Stop camera or another camera command takes over"
              onClick={() => patch({ durationMs: null })}>Continuous</button>
            <button className={!continuous ? 'on' : ''} disabled={!onChange}
              title="Runs for a fixed duration, then the camera is released"
              onClick={() => patch({ durationMs: a.durationMs ?? 2500 })}>Timed</button>
          </div>
        </Field>
      )}

      <SmallGrid>
        {f.includes('duration') && !(continuousCapable && continuous) && (
          // Authored in seconds for intuitiveness; stored in milliseconds
          // (the mp command contract).
          <Field label="Duration (s)" title="How long the motion or effect runs. A beat waits for its longest action, so this also paces the timeline.">
            <input className="oe-inp mono" type="number" step="any" value={(a.durationMs ?? 2000) / 1000} disabled={!onChange}
              onChange={(e) => patch({ durationMs: Math.round(parseFloat(e.target.value || '0') * 1000) })} />
          </Field>
        )}
        {f.includes('distance') && (
          // Authored in meters for intuitiveness; stored in centimeters (the
          // Unreal / mp command contract uses cm). 1 m = 100 cm. Empty = null,
          // which the viewer resolves to its own default (twin radius).
          // The input converts ft to m on commit, before the m to cm step.
          <Field label={`Distance (${lengthUnit})`} title={`How far from the twin the camera stays while orbiting or following, in ${unitWord}. Leave empty for the viewer default.`}>
            <LengthInput placeholder="default" metres={a.distance == null ? null : a.distance / 100} disabled={!onChange}
              onCommit={(m) => patch({ distance: m == null ? null : Math.round(m * 100) })} />
          </Field>
        )}
        {f.includes('pitch') && (
          <Field label="Pitch (°)" title="Camera angle above the target, in degrees: 0 is level with it, 90 looks straight down.">
            <input className="oe-inp mono" type="number" value={a.pitch ?? 20} disabled={!onChange}
              onChange={(e) => patch({ pitch: parseFloat(e.target.value) })} />
          </Field>
        )}
        {f.includes('rotateSpeed') && (
          // Authored in laps/minute (revolutions per minute) for intuitiveness;
          // stored as degrees/second (the mp command contract). 1 lap = 360°, so
          // deg/s = laps/min x 6, and laps/min = deg/s / 6. Default 15°/s = 2.5 laps/min.
          <Field label="Rotate (laps/min)" title="Orbit speed: full circles around the twin per minute.">
            <input className="oe-inp mono" type="number" step="any" value={(a.rotateSpeed ?? 15) / 6} disabled={!onChange}
              onChange={(e) => patch({ rotateSpeed: parseFloat(e.target.value) * 6 })} />
          </Field>
        )}
        {f.includes('opacity') && (
          <Field label="Opacity" title="0 = invisible, 1 = fully opaque. Setting 1 restores the twin's original materials. Without a duration the fade stays until restored.">
            <input className="oe-inp mono" type="number" step="any" value={a.opacity ?? 0.4} disabled={!onChange}
              onChange={(e) => patch({ opacity: parseFloat(e.target.value) })} />
          </Field>
        )}
      </SmallGrid>

      {f.includes('dither') && (
        <label
          className="oe-dither-toggle"
          title="Screen-door (dithered) transparency instead of smooth glass. Use ONLY for a model that flickers or shows doubled / see-through parts when faded — its geometry has overlapping or duplicate surfaces that smooth glass cannot render cleanly. It looks like a fine stipple, but is rock-steady."
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0' }}
        >
          <input
            type="checkbox"
            checked={a.dither}
            disabled={!onChange}
            onChange={(e) => patch({ dither: e.target.checked })}
          />
          <span>Dithered transparency</span>
        </label>
      )}
      {f.includes('dither') && a.dither && (
        <div className="oe-hint" style={{ margin: '0 0 6px' }}>
          <Icon of={Icons.status.warning} size={12} /> This model will fade with a dithered stipple, not smooth glass: use this only
          for a model that flickers or doubles up as smooth glass.
        </div>
      )}

      {/* Preview in the embedded 3D viewer — only for camera commands that have
          a viewer counterpart and an authored target (see previewableCommand).
          Present only while the 3D pane is active (onPreview supplied). */}
      {onPreview && previewableCommand(a) &&
        (previewActive ? (
          // A preview is running — Orbit/Follow until stopped, a fly/look for its
          // flight — so the button toggles to a Stop for ALL of them (also the
          // "is a preview running?" cue the author asked for).
          <button
            className="oe-btn sm active"
            style={{ marginTop: 8 }}
            onClick={() => onStopPreview?.()}
            title="Stop the running 3D preview"
          >
            <Icon of={Icons.action.stop} size={14} /> Stop Preview
          </button>
        ) : (
          <button
            className="oe-btn sm"
            style={{ marginTop: 8 }}
            onClick={() => onPreview(a)}
            title="Fly the embedded 3D camera through this command right now"
          >
            <Icon of={Icons.action.play} size={14} /> Preview in 3D
          </button>
        ))}
    </>
  );
}

function NarrateBody({ a, onChange, onPreview, onStopPreview, previewActive }: {
  a: NarrateAction;
  onChange?: (next: EditorAction) => void;
  onPreview?: (a: NarrateAction) => void;
  onStopPreview?: () => void;
  previewActive?: boolean;
}) {
  return (
    <>
      <Field label="Narration text">
        <textarea className="oe-inp" style={{ minHeight: 120 }} value={a.text} disabled={!onChange}
          onChange={(e) => onChange?.({ ...a, text: e.target.value })} />
      </Field>
      <div className="oe-hint" style={{ margin: '-6px 0 2px' }}>
        ≈ {(narrMs(a.text) / 1000).toFixed(1)}s spoken (estimate; real length comes from pre-warmed TTS). This drives the beat length.
      </div>
      {/* Preview the caption over the 3D viewer for the line's spoken length.
          Present only while the 3D pane is active (onPreview supplied). */}
      {onPreview && a.text.trim() && (
        previewActive ? (
          <button className="oe-btn active" style={{ width: '100%', marginBottom: 6 }} onClick={() => onStopPreview?.()}>
            <Icon of={Icons.action.stop} size={14} /> Stop Preview
          </button>
        ) : (
          <button className="oe-btn" style={{ width: '100%', marginBottom: 6 }} onClick={() => onPreview(a)}>
            <Icon of={Icons.action.play} size={14} /> Preview in 3D
          </button>
        )
      )}
      <Field label="Audio ref (optional)" title="Reference to a pre-generated audio asset for this line. Playback currently always speaks the text via live text-to-speech, so this can stay blank.">
        <input className="oe-inp" value={a.audioRef ?? ''} disabled={!onChange}
          onChange={(e) => onChange?.({ ...a, audioRef: e.target.value || null })} />
      </Field>
    </>
  );
}

function WaitBody({ a, onChange }: { a: WaitAction; onChange?: (next: EditorAction) => void }) {
  return (
    <>
      {/* Authored in seconds; stored in milliseconds. */}
      <Field label="Hold (s) — optional extra dwell on this beat">
        <input className="oe-inp mono" type="number" step="any" value={a.ms / 1000} disabled={!onChange}
          onChange={(e) => onChange?.({ ...a, ms: Math.round(parseFloat(e.target.value || '0') * 1000) })} />
      </Field>
      <div className="oe-hint" style={{ margin: '-6px 0 2px' }}>
        Only needed for deliberate silence or to hold a beat longer than its content.
      </div>
    </>
  );
}

/**
 * Comprehension check editor (MCQ). The correct answer is a flag on each
 * option here; the save path splits it back out into the module's separate
 * answer-key table. Single-select marks behave as radios (picking one clears
 * the rest), multi-select as checkboxes: the same grading the trainee sees.
 * Tries is a total count with an explicit Unlimited toggle (null); time limit
 * is an on/off toggle enabling a number (off saves null, untimed). Neither
 * uses a magic 0.
 */
function CheckEditor({ check, step, twinOptions, twinNames, selected3dTwin, onChange, onDelete, onPreview, onStopPreview, previewActive }: {
  check: EditorCheck;
  step: EditorStep;
  twinOptions: string[];
  twinNames?: Map<string, string>;
  selected3dTwin?: string | null;
  onChange?: (next: EditorCheck) => void;
  onDelete?: () => void;
  onPreview?: (c: EditorCheck) => void;
  onStopPreview?: () => void;
  previewActive?: boolean;
}) {
  const c = check;
  const world = isWorldSelection(c);
  const patch = (p: Partial<EditorCheck>) => onChange?.({ ...c, ...p });
  // Option edits go through the model: world selection derives multiSelect from
  // the set size and rule, and below two correct answers the rule falls back to All.
  const patchOptions = (options: EditorCheckOption[]) => onChange?.(withCheckOptions(c, options));
  const any = c.grading === 'Any';
  // Same rules the save enforces, on the check itself: the whole stop save is
  // refused while any of them is broken. Twin-existence is passed for World
  // selection so an expected twin that left the project is flagged here.
  const twinIdSet = new Set(twinOptions);
  const err = checkError(c, authoredBeats(step), twinIdSet);

  const markCorrect = (i: number) => patchOptions(
    c.options.map((o, j) => ({
      ...o,
      // Any accepts several correct options even in single-select.
      correct: c.multiSelect || any ? (i === j ? !o.correct : o.correct) : i === j,
    })),
  );
  // Leaving multi-select must not strand a second marked answer in a state the
  // save refuses: keep the first mark, drop the rest.
  const setMultiSelect = (multiSelect: boolean) => {
    if (multiSelect) { patch({ multiSelect }); return; }
    let kept = false;
    patch({
      multiSelect,
      options: c.options.map((o) => {
        const correct = o.correct && !kept;
        if (correct) kept = true;
        return { ...o, correct };
      }),
    });
  };

  return (
    <div className="oe-act">
      <div className="oe-act-head">
        <span className="oe-kind check" title="A question the trainee answers before this beat's playback continues">check</span>
        <span className="oe-lane-tag checks" title={LANES.find((l) => l.key === 'checks')?.desc}>Beat {c.beat + 1}</span>
        <span className="oe-spacer" />
        {onDelete && <button className="oe-ibtn del" aria-label="Delete check" title="Delete check" onClick={onDelete}><Icon of={Icons.action.delete} size={14} /></button>}
      </div>
      <div className="oe-act-body">
        <Field label="Check type" title="What kind of answer this check expects. Multiple choice grades a picked answer; Select in world grades the twins the trainee clicks in the 3D scene.">
          <SearchSelect
            value={c.checkType}
            disabled={!onChange || CHECK_TYPES.length === 1}
            options={CHECK_TYPES.map((t) => ({ value: t, label: CHECK_TYPE_LABEL[t] }))}
            onChange={(v) => onChange?.(switchCheckType(c, v as CheckTypeTag))}
          />
        </Field>

        <Field label="Question" title="The question shown to the trainee. The guide also reads it aloud when the check opens.">
          <textarea className="oe-inp" style={{ minHeight: 72 }} value={c.question} disabled={!onChange}
            onChange={(e) => patch({ question: e.target.value })} />
        </Field>

        {world ? (
          <Field
            label={c.options.length < 2 ? 'Expected twin · the trainee must click this one'
              : any ? 'Expected twins · the trainee clicks any one of these' : 'Expected twins · the trainee must click all of these'}
            title="The twin(s) the trainee has to select in the 3D scene. With two or more, choose below whether all of them or any one of them is the answer."
          >
            {worldSelectionTwinIds(c).map((id) => (
              <div key={id} className="oe-opt correct">
                <span className="ok" aria-hidden title="An expected twin"><Icon of={Icons.status.success} size={11} /></span>
                <input
                  value={twinNames?.get(id) ? `${twinNames.get(id)} · ${id}` : id}
                  disabled
                  title="An expected twin (added below)"
                  readOnly
                />
                {onChange && (
                  <button className="del" title="Remove this twin from the expected set"
                    onClick={() => patchOptions(c.options.filter((o) => o.id !== id))}><Icon of={Icons.action.remove} size={14} /></button>
                )}
              </div>
            ))}
            {onChange && (
              <div style={{ marginTop: 6 }}>
                <div className="oe-loc-row">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TwinSelect
                      value=""
                      twinOptions={twinOptions.filter((t) => !c.options.some((o) => o.id === t))}
                      twinNames={twinNames}
                      onChange={(twinId) => { if (twinId) onChange(addWorldSelectionTwin(c, twinId, twinNames?.get(twinId) ?? twinId)); }}
                    />
                  </div>
                  {selected3dTwin && (
                    <AssignSelectedButton
                      selected3dTwin={selected3dTwin}
                      twinNames={twinNames}
                      current={c.options.some((o) => o.id === selected3dTwin) ? selected3dTwin : ''}
                      onAssign={() => onChange(addWorldSelectionTwin(c, selected3dTwin, twinNames?.get(selected3dTwin) ?? selected3dTwin))}
                    />
                  )}
                </div>
                <div className="oe-hint" style={{ marginTop: 4 }}>
                  Double-click a twin in the 3D view to add it to the expected set, or search below, or select one and use the Assign selected twin button.
                </div>
              </div>
            )}
          </Field>
        ) : (
        <Field
          label={c.multiSelect || any ? 'Options · click to mark every correct answer' : 'Options · click to mark the correct answer'}
          title="The answers the trainee picks from. With two or more marked, choose below whether all of them or any one of them is the answer."
        >
          {c.options.map((o, i) => (
            <div key={o.id} className={'oe-opt' + (o.correct ? ' correct' : '')}>
              <button
                className={'ok' + (c.multiSelect || any ? ' multi' : '')}
                disabled={!onChange}
                aria-pressed={o.correct}
                title="Mark this option as the correct answer"
                onClick={() => markCorrect(i)}
              ><Icon of={Icons.status.success} size={11} /></button>
              <input
                value={o.text}
                disabled={!onChange}
                placeholder={`Option ${i + 1}`}
                title="Answer text shown to the trainee"
                onChange={(e) => patchOptions(c.options.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
              />
              {onChange && (
                <button className="del" title="Remove this option"
                  onClick={() => patchOptions(c.options.filter((_, j) => j !== i))}><Icon of={Icons.action.remove} size={14} /></button>
              )}
            </div>
          ))}
          {onChange && (
            <button
              className="oe-btn sm"
              style={{ width: '100%' }}
              title="Add another answer option"
              onClick={() => patchOptions([...c.options, { id: uid('opt'), text: '', correct: false }])}
            >+ Add option</button>
          )}
        </Field>
        )}

        {hasGradingChoice(c) && (
          <Field
            label={world ? 'Trainee must select' : 'Trainee must mark'}
            title="All of these: the answer is the whole set. Any one of these: one correct pick passes and a wrong pick fails; the trainee picks a single answer."
          >
            <div className="oe-seg" role="group" aria-label="Grading rule">
              {(['All', 'Any'] as const).map((g) => (
                <button key={g} type="button" className={c.grading === g ? 'on' : ''} aria-pressed={c.grading === g}
                  disabled={!onChange} onClick={() => onChange?.(setCheckGrading(c, g))}>
                  {g === 'All' ? 'All of these' : 'Any one of these'}
                </button>
              ))}
            </div>
          </Field>
        )}

        {err && <div className="oe-error" role="alert">{err}</div>}

        {!world && (
          <Switch
            label="Multi-select"
            title={any
              ? '"Any one of these" is single-select: the trainee picks one answer. Choose "All of these" to allow several.'
              : 'Allow the trainee to select more than one answer. Grading requires exactly the marked set.'}
            on={c.multiSelect}
            onChange={onChange && !any ? setMultiSelect : undefined}
          />
        )}
        {!world && (
          <Switch
            label="Shuffle options"
            title="Randomize the answer order when the check first opens. The order stays fixed across retries of the same question."
            on={c.shuffleOptions}
            onChange={onChange ? (v) => patch({ shuffleOptions: v }) : undefined}
          />
        )}
        <Switch
          label="Show correct answer"
          title="Reveal the correct answer to the trainee once the check resolves (answered correctly or retries used up)."
          on={c.showCorrectAnswer}
          onChange={onChange ? (v) => patch({ showCorrectAnswer: v }) : undefined}
        />

        <div className="oe-row2">
          <TriesField
            retryLimit={c.retryLimit}
            onChange={onChange ? (v) => patch({ retryLimit: v }) : undefined}
          />
          <OptionalNumber
            label="Time limit (s)"
            title="Seconds the trainee has to answer. Off = no time limit. Running out uses a try."
            inputTitle="Seconds the trainee has to answer"
            value={c.timeLimitSeconds}
            offPlaceholder="no limit"
            defaultValue={45}
            onChange={onChange ? (v) => patch({ timeLimitSeconds: v }) : undefined}
          />
        </div>

        {/* Preview the check as playback presents it (MCQ card / World-selection
            scene prompt), graded in the browser. Present only while the 3D pane
            is active (onPreview supplied). */}
        {onPreview && (
          previewActive ? (
            <button className="oe-btn active" style={{ width: '100%', marginTop: 10 }} onClick={() => onStopPreview?.()}>
              <Icon of={Icons.action.stop} size={14} /> Stop Preview
            </button>
          ) : (
            <button className="oe-btn" style={{ width: '100%', marginTop: 10 }} onClick={() => onPreview(c)}>
              <Icon of={Icons.action.play} size={14} /> Preview in 3D
            </button>
          )
        )}

        <div className="oe-hint" style={{ marginTop: 8 }}>
          All tries (passed, failed, expired) are recorded per user presence for the review queue.
        </div>
      </div>
    </div>
  );
}

/** Label + pill toggle row (the mockup's `.switch`). */
function Switch({ label, title, on, onChange }: {
  label: string; title: string; on: boolean; onChange?: (v: boolean) => void;
}) {
  return (
    <button
      className="oe-switch"
      type="button"
      title={title}
      aria-pressed={on}
      disabled={!onChange}
      onClick={() => onChange?.(!on)}
    >
      <span>{label}</span>
      <span className={'oe-tgl' + (on ? ' on' : '')} aria-hidden />
    </button>
  );
}

/**
 * An optional number: a toggle in the label enables the field; off saves null.
 * Never a magic 0: 0 retries is a real, different setting from unlimited.
 */
/**
 * How many tries the trainee gets, as the TOTAL count a manager thinks in
 * ("one try"), not the module's `retry_limit` (extra tries after the first:
 * tries - 1). The number is the normal case and 1 the default; unlimited is
 * the explicit toggle, because a toggle whose OFF state meant "unlimited" read
 * as the opposite of what it did.
 */
function TriesField({ retryLimit, onChange }: {
  retryLimit: number | null;
  onChange?: (v: number | null) => void;
}) {
  const unlimited = retryLimit == null;
  return (
    <div className="oe-field">
      <label title="How many tries the trainee gets in all. A try is used by a wrong submission or by the timer running out; after the last one the question is marked as missed and the orientation moves on. Switch on Unlimited to let them keep trying until they get it right.">
        Tries
        <span className="oe-tgl-label" title="Unlimited tries: only a correct answer settles the question">
          unlimited
          <button
            className={'oe-tgl sm' + (unlimited ? ' on' : '')}
            type="button"
            aria-pressed={unlimited}
            aria-label={`Unlimited tries: ${unlimited ? 'on' : 'off'}`}
            disabled={!onChange}
            onClick={() => onChange?.(unlimited ? 0 : null)}
          />
        </span>
      </label>
      <input
        className="oe-inp mono"
        type="number"
        min={1}
        value={unlimited ? '' : retryLimit + 1}
        placeholder="unlimited"
        disabled={!onChange || unlimited}
        title="Total tries, including the first"
        onChange={(e) => onChange?.(Math.max(1, parseInt(e.target.value, 10) || 1) - 1)}
      />
    </div>
  );
}

function OptionalNumber({ label, title, inputTitle, value, offPlaceholder, defaultValue, onChange }: {
  label: string; title: string; inputTitle: string;
  value: number | null; offPlaceholder: string; defaultValue: number;
  onChange?: (v: number | null) => void;
}) {
  const on = value != null;
  return (
    <div className="oe-field">
      <label title={title}>
        {label}
        <button
          className={'oe-tgl sm' + (on ? ' on' : '')}
          type="button"
          aria-pressed={on}
          aria-label={`${label}: ${on ? 'on' : 'off'}`}
          disabled={!onChange}
          onClick={() => onChange?.(on ? null : defaultValue)}
        />
      </label>
      <input
        className="oe-inp mono"
        type="number"
        min={0}
        value={value ?? ''}
        placeholder={offPlaceholder}
        disabled={!onChange || !on}
        title={inputTitle}
        onChange={(e) => onChange?.(e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0))}
      />
    </div>
  );
}

/**
 * Searchable twin picker shared by the Target-twin field and Fly + look's
 * CommandTarget rows. A saved target absent from the project's live twins
 * (renamed, removed, or stale authoring like the old 'MineZero' default) is
 * prepended for THIS control only, labeled so the author knows to retarget
 * it — it never joins the general list.
 */
function TwinSelect({ value, twinOptions, twinNames, staleNote, onChange }: {
  value: string;
  twinOptions: string[];
  twinNames?: Map<string, string>;
  /** Label suffix for a saved value absent from the (possibly capability-
   *  filtered) options, e.g. '— not a dynamic twin'. Default: not in project. */
  staleNote?: string;
  onChange?: (twinId: string) => void;
}) {
  const stale = Boolean(value) && !twinOptions.includes(value);
  const opts = stale ? [value, ...twinOptions] : twinOptions;
  return (
    <SearchSelect
      value={value}
      disabled={!onChange}
      placeholder="Search twins…"
      options={opts.map((t) => {
        // Show the twin's displayName property when it has one; the id stays
        // searchable (SearchSelect matches value too).
        const dn = twinNames?.get(t);
        const base = dn ? `${dn} · ${t}` : t;
        return {
          value: t,
          label: stale && t === value ? `${base} ${staleNote ?? '— not in this project'}` : base,
        };
      })}
      onChange={(v) => onChange?.(v)}
    />
  );
}

/**
 * Twin-or-location editor for one Fly + look CommandTarget (the module's
 * sum type: Twin(id) | Location(lat/lon/alt)). Switching to Location seeds
 * the stop's coords so the row is immediately valid; switching to Twin keeps
 * a previously chosen twin.
 */
function TargetField({ label, title, value, step, twinOptions, twinNames, selected3dTwin, onChange, onPick, picking, pinTone }: {
  label: string;
  title?: string;
  value: CommandTarget | null;
  step: EditorStep | null;
  twinOptions: string[];
  twinNames?: Map<string, string>;
  selected3dTwin?: string | null;
  onChange?: (t: CommandTarget) => void;
  onPick?: () => void;
  picking?: boolean;
  /** Passed to the Location readout's pin — 'fly' tints it green. */
  pinTone?: 'fly';
}) {
  const isLoc = value?.tag === 'Location';
  return (
    <Field label={label} title={title}>
      <div className="oe-toggle2" style={{ marginBottom: 6 }}>
        <button className={!isLoc ? 'on' : ''} disabled={!onChange}
          title="Target a twin"
          onClick={() => onChange?.({ tag: 'Twin', value: value?.tag === 'Twin' ? value.value : '' })}>Twin</button>
        <button className={isLoc ? 'on' : ''} disabled={!onChange}
          title="Target a map coordinate (seeds with the stop's location)"
          onClick={() => onChange?.({ tag: 'Location', value: { latitude: step?.lat ?? 0, longitude: step?.lon ?? 0, altitude: step?.el } })}>Location</button>
      </div>
      {isLoc ? (
        <LocationField
          lat={value.value.latitude}
          lon={value.value.longitude}
          alt={value.value.altitude ?? step?.el ?? 0}
          onChange={onChange ? (v) => onChange({ tag: 'Location', value: { latitude: v.lat, longitude: v.lon, altitude: v.alt } }) : undefined}
          onPick={onPick}
          picking={picking}
          pinTone={pinTone}
        />
      ) : (
        <div className="oe-loc-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <TwinSelect
              value={value?.tag === 'Twin' ? value.value : ''}
              twinOptions={twinOptions}
              twinNames={twinNames}
              onChange={onChange ? (v) => onChange({ tag: 'Twin', value: v }) : undefined}
            />
          </div>
          {onChange && selected3dTwin && (
            <AssignSelectedButton
              selected3dTwin={selected3dTwin}
              twinNames={twinNames}
              current={value?.tag === 'Twin' ? value.value : ''}
              onAssign={() => onChange({ tag: 'Twin', value: selected3dTwin })}
            />
          )}
        </div>
      )}
    </Field>
  );
}

/**
 * Icon-only "assign from the 3D selection" button beside a twin selector,
 * styled like the pick-in-scene button. Lets an author retarget an EXISTING
 * beat from the 3D view (new beats auto-seed from the selection; this covers
 * editing). Render only in 3D (selected3dTwin supplied); its arrow points
 * into the selector, and it goes active/disabled once that twin is already
 * the target.
 */
function AssignSelectedButton({ selected3dTwin, twinNames, current, onAssign }: {
  selected3dTwin: string;
  twinNames?: Map<string, string>;
  current: string;
  onAssign: () => void;
}) {
  const dn = twinNames?.get(selected3dTwin);
  const label = dn ? `${dn} · ${selected3dTwin}` : selected3dTwin;
  const already = selected3dTwin === current;
  return (
    <button
      className={'oe-geo-pick' + (already ? ' on' : '')}
      disabled={already}
      onClick={onAssign}
      title={already ? `Already targeting ${label}` : `Assign selected twin: ${label}`}
      aria-label="Assign the twin selected in the 3D view as this target"
    >
      <Icon of={Icons.content.twin} />
    </button>
  );
}

/**
 * Multi-location editor for FlyToLocation ("fly along path"). A single point is a
 * normal fly-to; add waypoints and the camera flies them in order. Each node
 * reuses the same pick-in-scene / place-at-camera workflow as the single
 * Location field — "Pick" arms a ground click for that node (lit while armed),
 * "Camera" drops the node at the viewer camera's own position (for underground
 * drift paths). Every waypoint is listed and independently editable/removable,
 * so you move between nodes by acting on each row. An empty command shows one
 * seed row from the stop so there is always something to pick into.
 */
function PathField({ a, step, twinNames, lineStringsByTwin, onChange, onPickNode, onPlaceAtCamera, activeIndex }: {
  a: CommandAction;
  step: EditorStep | null;
  twinNames?: Map<string, string>;
  lineStringsByTwin?: Map<string, { name: string; points: CommandLocation[] }[]>;
  onChange?: (next: EditorAction) => void;
  onPickNode?: (index: number) => void;
  onPlaceAtCamera?: (index: number) => void;
  activeIndex?: number | null;
}) {
  const editable = !!onChange;
  const fallbackAlt = step?.el ?? 0;
  // Source toggle: draw/pick waypoints by hand, or import a path twin's
  // LineString spline. Local state (PathField is remounted per selection via the
  // ActionEditor key, so this starts fresh for each command).
  const twinsWithLine = lineStringsByTwin ? [...lineStringsByTwin.keys()] : [];
  // Source provenance is PERSISTED in the (otherwise-unused-for-FlyToLocation)
  // twinIds / propertyNames fields so re-opening the command remembers it was
  // imported from a twin and which line. `commandTargetTwin` ignores twinIds for
  // FlyToLocation (CMD_FIELDS has no 'twin'), so this triggers no highlight.
  // Initialised from those fields at mount (PathField remounts per selection).
  const [source, setSource] = useState<'draw' | 'twin'>(a.twinIds[0] ? 'twin' : 'draw');
  const [importTwin, setImportTwin] = useState(a.twinIds[0] ?? '');
  // Which LineString property of `importTwin` is chosen (a twin can have several).
  const [importProp, setImportProp] = useState(a.propertyNames[0] ?? '');
  // Twin-sourced waypoints mirror the twin's spline, so the per-waypoint list is
  // HIDDEN entirely (the spline is still stored in `path` and flies) — the author
  // edits the path by editing the twin, or switches to Location to draw by hand.
  // (Guarded on an available line twin so an orphaned snapshot — its twin gone —
  // still shows its points rather than hiding them behind a vanished toggle.)
  const locked = source === 'twin' && twinsWithLine.length > 0;
  const commitImport = (tid: string, propName?: string) => {
    const list = lineStringsByTwin?.get(tid) ?? [];
    const ls = propName ? list.find((l) => l.name === propName) : list[0];
    if (ls && ls.points.length >= 2) {
      // Persist the provenance (twin + line) alongside the imported waypoints.
      onChange?.({ ...withCommandPoints(a, ls.points), twinIds: [tid], propertyNames: [ls.name] });
    }
  };
  const stored = commandPoints(a);
  // Always show at least one row (seeded from the stop) so a brand-new command
  // has somewhere to pick into; it only becomes real once edited/picked.
  const rows = stored.length
    ? stored
    : [{ latitude: step?.lat ?? 0, longitude: step?.lon ?? 0, altitude: fallbackAlt }];
  // Collapse the per-waypoint list by default once it's long (e.g. an imported
  // spline of dozens of points) so it doesn't flood the inspector; small paths
  // start expanded. Per-selection state (PathField remounts on the ActionEditor key).
  const [pointsOpen, setPointsOpen] = useState(() => rows.length <= 6);
  const commit = (next: CommandLocation[]) => onChange?.(withCommandPoints(a, next));
  const writeNode = (i: number, lat: number, lon: number, alt: number) =>
    commit(rows.map((p, j) => (j === i ? { latitude: lat, longitude: lon, altitude: alt } : p)));
  // Manual edit: when the node's lat/lon moves, snap its altitude onto the
  // terrain surface (never below it) by sampling the DEM — the same source 2D
  // stop placement uses. A direct altitude tweak (lat/lon unchanged) is kept as
  // typed. Camera placement and twin import set their own altitudes elsewhere.
  const setNode = (i: number, v: { lat: number; lon: number; alt: number }) => {
    const prev = rows[i];
    const moved = !prev || prev.latitude !== v.lat || prev.longitude !== v.lon;
    if (!moved) { writeNode(i, v.lat, v.lon, v.alt); return; }
    void elevationAt(v.lat, v.lon).then((el) => writeNode(i, v.lat, v.lon, el ?? v.alt));
  };
  const addNode = () => {
    const last = rows[rows.length - 1];
    const lat = last.latitude;
    const lon = last.longitude;
    // New waypoint sits on the terrain at the previous point's spot (the author
    // then picks/moves it). Commit once the DEM resolves so it never seeds below.
    void elevationAt(lat, lon).then((el) =>
      commit([...rows, { latitude: lat, longitude: lon, altitude: el ?? last.altitude ?? fallbackAlt }]));
  };
  const delNode = (i: number) => commit(rows.filter((_, j) => j !== i));

  return (
    <Field
      label={rows.length > 1 ? `Path · ${rows.length} waypoints` : 'Location'}
      title="Where the camera flies. One point is a normal fly-to; add waypoints to fly a path through them in order. Pick each point in the scene, or drop it at the camera's own position for an underground route."
    >
      {/* Source: hand-draw waypoints, or import a path twin's LineString spline.
          Only shown when the project actually has a LineString twin to import. */}
      {editable && twinsWithLine.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div className="oe-toggle2" style={{ marginBottom: source === 'twin' ? 6 : 0 }}>
            <button className={source === 'draw' ? 'on' : ''} title="Draw / pick the waypoints by hand (drops the twin link)"
              onClick={() => {
                setSource('draw');
                // Going manual drops the twin-source provenance (keeps the points).
                if (a.twinIds.length || a.propertyNames.length) onChange?.({ ...a, twinIds: [], propertyNames: [] });
              }}>Location</button>
            <button className={source === 'twin' ? 'on' : ''} title="Import the spline of a twin that has a LineString geometry"
              onClick={() => setSource('twin')}>Twin</button>
          </div>
          {source === 'twin' && (() => {
            const lines = importTwin ? (lineStringsByTwin?.get(importTwin) ?? []) : [];
            return (
              <>
                <TwinSelect
                  value={importTwin}
                  twinOptions={twinsWithLine}
                  twinNames={twinNames}
                  staleNote="— no LineString geometry"
                  onChange={(tid) => {
                    setImportTwin(tid);
                    const list = lineStringsByTwin?.get(tid) ?? [];
                    // One line → import it now; several → let the user pick which.
                    if (list.length === 1) { setImportProp(list[0].name); commitImport(tid, list[0].name); }
                    else setImportProp('');
                  }}
                />
                {lines.length > 1 && (
                  <div style={{ marginTop: 6 }}>
                    <SearchSelect
                      value={importProp}
                      placeholder="Pick which path line…"
                      options={lines.map((l) => ({ value: l.name, label: `${l.name} · ${l.points.length} pts` }))}
                      onChange={(name) => { setImportProp(name); commitImport(importTwin, name); }}
                    />
                  </div>
                )}
                <div className="oe-hint" style={{ marginTop: 4 }}>
                  {lines.length > 1
                    ? 'This twin has several path lines — choose which one. Its spline is the waypoints below, locked to the twin. Switch to Location to edit them.'
                    : "Picking a path twin uses its spline (with altitude) as the waypoints below, locked to the twin. Switch to Location to edit them."}
                </div>
              </>
            );
          })()}
        </div>
      )}
      {/* Reverse: fly the waypoints end→start. The Start/End 3D markers and the
          preview/play flight both follow this direction. */}
      {editable && rows.length >= 2 && (
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0 6px', fontSize: '0.75rem' }}
          title="Fly the path in reverse — from the last waypoint to the first. The Start/End markers swap to match."
        >
          <input type="checkbox" checked={a.reverse} onChange={(e) => onChange?.({ ...a, reverse: e.target.checked })} />
          <span>Reverse direction</span>
        </label>
      )}
      {/* In Twin mode the waypoint list is hidden entirely — the spline is stored
          and flies, but the author edits the path via the twin, not point-by-point.
          Location mode shows the editable, collapsible per-waypoint list. */}
      {!locked && (
        <>
          {/* Collapsible waypoint list — one header for the whole array; the
              individual Point rows hide when collapsed. */}
          <button
            type="button"
            className="oe-sect-head"
            aria-expanded={pointsOpen}
            style={{ width: '100%', marginBottom: pointsOpen ? 6 : 0 }}
            onClick={() => setPointsOpen((o) => !o)}
            title={pointsOpen ? 'Collapse the waypoint list' : 'Expand the waypoint list'}
          >
            <span className={'chev' + (pointsOpen ? ' open' : '')} aria-hidden><Icon of={PortalIcons.action.chevronRight} size={12} /></span>
            Waypoints · {rows.length}
          </button>
          {pointsOpen && (
            <>
              {rows.map((p, i) => (
                <div key={i} className="oe-path-node" style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span className="oe-hint" style={{ fontWeight: 600 }}>Point {i + 1}</span>
                    <span className="oe-spacer" style={{ flex: 1 }} />
                    {editable && onPlaceAtCamera && (
                      <button className="oe-btn ghost sm" title="Drop this waypoint at the camera's own position (for underground drift paths)" onClick={() => onPlaceAtCamera(i)}>Camera</button>
                    )}
                    {editable && rows.length > 1 && (
                      <button className="del" title="Remove this waypoint" onClick={() => delNode(i)}><Icon of={Icons.action.remove} size={14} /></button>
                    )}
                  </div>
                  <LocationField
                    lat={p.latitude}
                    lon={p.longitude}
                    alt={p.altitude ?? fallbackAlt}
                    onChange={editable ? (v) => setNode(i, v) : undefined}
                    onPick={onPickNode ? () => onPickNode(i) : undefined}
                    picking={activeIndex === i}
                  />
                </div>
              ))}
              {editable && (
                <button className="oe-btn sm" style={{ width: '100%' }} title="Add another waypoint to the path" onClick={addNode}>
                  + Add waypoint
                </button>
              )}
            </>
          )}
          <div className="oe-hint" style={{ marginTop: 4 }}>
            {rows.length > 1
              ? 'The camera flies these points in order (altitudes as authored — pick with Camera for an underground route).'
              : 'One point = a normal fly-to. Add a waypoint to fly a path.'}
          </div>
        </>
      )}
    </Field>
  );
}

/**
 * The unified location control (stop location + geo commands): a compact
 * readout (a location pin, `lat, lon · alt m`) that expands into Lat/Lon/Alt fields when
 * clicked, with an icon-only pick-in-scene button on the same row. Read-only
 * when `onChange` is absent; the pick button renders only when `onPick` is
 * supplied.
 */
function LocationField({ lat, lon, alt, onChange, onPick, picking, pinTone }: {
  lat: number; lon: number; alt: number;
  onChange?: (v: { lat: number; lon: number; alt: number }) => void;
  onPick?: () => void;
  picking?: boolean;
  /** 'fly' tints the location pin green to match the 3D fly marker; default is amber. */
  pinTone?: 'fly';
}) {
  const [editing, setEditing] = useState(false);
  const editRef = useRef<HTMLDivElement | null>(null);
  const { lengthUnit, fmtElevation } = useUnits();
  const patch = (p: Partial<{ lat: number; lon: number; alt: number }>) =>
    onChange?.({ lat, lon, alt, ...p });
  return (
    <div className="oe-loc-row">
      {editing && onChange ? (
        <div
          className="oe-loc-edit"
          ref={editRef}
          // Collapse back to the readout once focus leaves all three fields.
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEditing(false);
          }}
        >
          <label>Lat
            <input className="oe-inp mono" type="number" step="any" autoFocus value={lat}
              onChange={(e) => patch({ lat: parseFloat(e.target.value) })} />
          </label>
          <label>Lon
            <input className="oe-inp mono" type="number" step="any" value={lon}
              onChange={(e) => patch({ lon: parseFloat(e.target.value) })} />
          </label>
          <label>Alt ({lengthUnit})
            <LengthInput metres={alt} onCommit={(m) => { if (m != null) patch({ alt: m }); }} />
          </label>
        </div>
      ) : (
        <div
          className={'oe-geo' + (onChange ? ' editable' : '')}
          title={onChange ? 'Click to edit' : undefined}
          onClick={onChange ? () => setEditing(true) : undefined}
        >
          <span className={'pin' + (pinTone === 'fly' ? ' pin-fly' : '')}><Icon of={Icons.content.location} size={14} /></span>
          {lat.toFixed(5)}, {lon.toFixed(5)} · {fmtElevation(alt)}
          {onChange && <span className="hint">edit</span>}
        </div>
      )}
      {onPick && (
        <button
          className={'oe-geo-pick' + (picking ? ' on' : '')}
          title={picking ? 'Click in the 2D map or 3D view to set this location (click again to cancel)' : 'Pick in scene'}
          aria-label="Pick in scene"
          aria-pressed={!!picking}
          onClick={onPick}
        >
          <Icon of={Icons.content.location} />
        </button>
      )}
    </div>
  );
}

/**
 * Number input in the preferred length unit over a stored metre value.
 * Converts on commit (blur or Enter), not per keystroke, so a cm-rounded
 * field never fights the user mid-typing. Empty commits null.
 */
function LengthInput({ metres, onCommit, placeholder, disabled }: {
  metres: number | null;
  onCommit: (m: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { units, fromMetres, toMetres } = useUnits();
  const [draft, setDraft] = useState<string | null>(null);
  // Imperial shows 2 dp so a converted value does not read 82.02099737...
  const shown = metres == null ? '' : String(units === 'imperial' ? +fromMetres(metres).toFixed(2) : metres);
  const commit = () => {
    if (draft == null) return;
    const v = parseFloat(draft);
    setDraft(null);
    onCommit(Number.isFinite(v) ? toMetres(v) : null);
  };
  return (
    <input className="oe-inp mono" type="number" step="any" placeholder={placeholder} disabled={disabled}
      value={draft ?? shown}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
  );
}

/** Collapsible inspector section (Details / Selected action). Open by
 *  default; state is per-mount — a fresh inspector starts expanded. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="oe-sect">
      <button className="oe-sect-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={'chev' + (open ? ' open' : '')} aria-hidden><Icon of={PortalIcons.action.chevronRight} size={12} /></span>
        {title}
      </button>
      {open && <div className="oe-sect-body">{children}</div>}
    </div>
  );
}

/** `title` renders as hover help on the label (dotted underline via CSS). */
function Field({ label, title, children }: { label: string; title?: string; children: React.ReactNode }) {
  return (
    <div className="oe-field">
      <label title={title}>{label}</label>
      {children}
    </div>
  );
}

function SmallGrid({ children }: { children: React.ReactNode }) {
  const arr = Array.isArray(children) ? children.filter(Boolean) : [children];
  if (arr.length === 0) return null;
  return <div className={arr.length >= 3 ? 'oe-row3' : 'oe-row2'}>{children}</div>;
}

/**
 * Multi-select name picker for ShowTwinInfo's Properties / Telemetries arrays:
 * chips for the current selections (a remove button on each) plus a SearchSelect that lists
 * the twin's still-unpicked names and appends one on pick. A saved name no longer
 * on the twin shows as a labeled stale chip so it stays visible to fix.
 */
function NameMultiPicker({ label, title, selected, available, placeholder, disabled, onChange }: {
  label: string;
  title?: string;
  selected: string[];
  available: string[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const remaining = available.filter((n) => !selected.includes(n));
  return (
    <Field label={label} title={title}>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
          {selected.map((n) => {
            const stale = available.length > 0 && !available.includes(n);
            return (
              <span
                key={n}
                title={stale ? `${n} — not on this twin` : n}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '1px 6px', borderRadius: 10, fontSize: 12,
                  background: 'rgba(127,127,127,0.18)', opacity: stale ? 0.7 : 1,
                }}
              >
                {n}{stale && <Icon of={Icons.status.warning} size={12} />}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`Remove ${n}`}
                  onClick={() => onChange(selected.filter((x) => x !== n))}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                >
                  <Icon of={Icons.action.remove} size={12} />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <SearchSelect
        value=""
        disabled={disabled || remaining.length === 0}
        placeholder={remaining.length ? (placeholder ?? 'Add…') : '— none left'}
        options={remaining.map((n) => ({ value: n, label: n }))}
        onChange={(v) => {
          if (v && !selected.includes(v)) onChange([...selected, v]);
        }}
      />
    </Field>
  );
}
